import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPackageUpdatedDate } from '../../features/Package/packageAction';
import { PinnedFeatureUsage } from '../../modules/Accounts';
import usePersona from '../../modules/Dashboard/components/usePersona';
import useToggle from '../../hooks/useToggle';
import useAuthorization from '../../hooks/useAuthorization';
import { fetchTenantInfo } from '../../features/Tenant/tenantAction';
import { getAccountsWithLicenseList, getPackageList } from '../../features';
import { DASHBOARD_CARDS_ID, DASHBOARD_PERSONA } from '../../utils/constants';
import { DashboardHealthScore } from '../DashboardHealthScore';
import ActiveUsers from '../../modules/Accounts/components/ActiveUsers/ActiveUsers';
import VersionsCard from '../../modules/Versions/components/VersionsCard/VersionsCard';
import { TopFeatures } from '../TopFeatures';
import { LicenseUtilization } from '../AccountLicense';

export default function useDashboard() {
  const [refreshAccountList, setRefreshAccountList] = useToggle();
  const [refreshUsers, setRefreshUsers] = useToggle();
  const { userInfo } = useAuthorization();
  const { personaList, selectedPersona, handlePersonaChange } = usePersona();
  const packageListData = useSelector((state) => state.packageList?.packageMenuList);
  const packageUpdatedDate = useSelector((state) => state.packageUpdatedDate?.data);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getPackageList());
    dispatch(getPackageUpdatedDate());
  }, []);

  useEffect(() => {
    dispatch(getAccountsWithLicenseList());
  }, [refreshAccountList]);

  useEffect(() => {
    if (userInfo?.tenantId) {
      dispatch(fetchTenantInfo({ tenantId: userInfo.tenantId }));
    }
  }, [userInfo?.tenantId]);

  const visibleBoxIds = DASHBOARD_PERSONA[selectedPersona] || [];

  const boxComponents = {
    [DASHBOARD_CARDS_ID.HIGH_RISK_ACCOUNTS]: <DashboardHealthScore type="riskAccounts" title="High-Risk Accounts" refresh={setRefreshAccountList} />,
    [DASHBOARD_CARDS_ID.EXPIRING_ACCOUNTS]: <DashboardHealthScore type="expiredAccounts" title="Accounts Expiring" refresh={setRefreshAccountList} />,
    [DASHBOARD_CARDS_ID.DECLINING_ACCOUNTS]: <DashboardHealthScore type="decliningAccounts" title="Accounts with Declining KPI Scores" refresh={setRefreshAccountList} />,
    [DASHBOARD_CARDS_ID.TRENDING_ACCOUNTS]: <DashboardHealthScore type="trendingAccounts" title="Accounts with Trending KPI Scores" refresh={setRefreshAccountList} />,
    [DASHBOARD_CARDS_ID.ACTIVE_USERS]: <ActiveUsers more page="dashboard" refreshUsers={refreshUsers} setRefreshUsers={setRefreshUsers} />,
    [DASHBOARD_CARDS_ID.VERSIONS]: <VersionsCard packageList={packageListData} more />,
    [DASHBOARD_CARDS_ID.TOP_FEATURES]: <TopFeatures />,
    [DASHBOARD_CARDS_ID.LICENSE_ASSIGNMENT]: <LicenseUtilization title="Account License Assignment" refresh={setRefreshAccountList} />,
    [DASHBOARD_CARDS_ID.PINNED_FEATURES]: <PinnedFeatureUsage tenantId={userInfo?.tenantId} />,
  };

  return {
    personaList,
    selectedPersona,
    handlePersonaChange,
    visibleBoxIds,
    boxComponents,
    packageUpdatedDate,
  };
}
